import { useState, useEffect, useRef } from "react";
import {
  Mail, Zap, Shield, BookOpen, Check, ArrowRight,
  Menu, X, RefreshCw, ChevronRight, Sparkles,
  ShoppingBag, Lock, ChevronLeft, Pause, Play,
} from "lucide-react";
import "./index.css";

// ─────────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────────
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}


// Typewriter: starts after `startDelay` ms, types at `charDelay` ms/char
function useTypewriter(text: string, charDelay = 22, startDelay = 0) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    let iv: ReturnType<typeof setInterval>;
    const to = setTimeout(() => {
      iv = setInterval(() => {
        setIdx(i => { if (i >= text.length) { clearInterval(iv); return i; } return i + 1; });
      }, charDelay);
    }, startDelay);
    return () => { clearTimeout(to); clearInterval(iv); };
  }, [text, charDelay, startDelay]);
  return text.slice(0, idx);
}

// Show element after `delay` ms (resets on remount)
function useShow(delay: number) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, []);
  return show;
}

// ─────────────────────────────────────────────────────────────────
// WAITLIST FORM
// ─────────────────────────────────────────────────────────────────
type WaitlistState = "idle" | "loading" | "success" | "duplicate" | "error";

function WaitlistForm({ source = "landing", large = false }: { source?: string; large?: boolean }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<WaitlistState>("idle");
  const [errMsg, setErrMsg] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setState("loading");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source }),
      });
      const data = await res.json();
      if (res.ok) setState("success");
      else if (data.error === "already_subscribed") setState("duplicate");
      else { setErrMsg(data.error ?? "Something went wrong"); setState("error"); }
    } catch {
      setErrMsg("Network error — please try again.");
      setState("error");
    }
  }

  if (state === "success") return (
    <div className="flex items-center gap-3 py-2" style={{ animation: "rowAppear 0.4s ease both" }}>
      <div className="w-8 h-8 rounded-full bg-r-green-dim border border-r-green/30 flex items-center justify-center shrink-0">
        <Check size={14} strokeWidth={2.5} className="text-r-green" />
      </div>
      <div>
        <p className={`font-body font-semibold text-r-ink ${large ? "text-[16px]" : "text-[14px]"}`}>You're on the list!</p>
        <p className="text-[12px] font-code text-r-dim">We'll reach out the moment Resolved launches.</p>
      </div>
    </div>
  );

  if (state === "duplicate") return (
    <div className="flex items-center gap-3 py-2" style={{ animation: "rowAppear 0.4s ease both" }}>
      <div className="w-8 h-8 rounded-full bg-r-blue-dim border border-r-blue/20 flex items-center justify-center shrink-0">
        <Mail size={14} className="text-r-blue" />
      </div>
      <p className={`font-body text-r-muted ${large ? "text-[15px]" : "text-[14px]"}`}>
        Already on the list — we'll be in touch!
      </p>
    </div>
  );

  return (
    <form onSubmit={submit} className="w-full">
      <div className={`flex flex-col sm:flex-row ${large ? "gap-3" : "gap-2"}`}>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          className={`flex-1 border border-r-border rounded-xl font-body text-r-text
                      placeholder:text-r-dim bg-white transition-all
                      focus:outline-none focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)]
                      ${large ? "px-5 py-3.5 text-[15px]" : "px-4 py-2.5 text-[14px]"}`}
        />
        <button
          type="submit"
          disabled={state === "loading"}
          className={`btn-primary flex items-center justify-center gap-2 font-body whitespace-nowrap
                      disabled:opacity-60 disabled:cursor-not-allowed
                      ${large ? "px-7 py-3.5 text-[15px]" : "px-5 py-2.5 text-[14px]"}`}
        >
          {state === "loading"
            ? <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            : <>{large ? "Join the waitlist" : "Join waitlist"} <ArrowRight size={large ? 16 : 14} /></>
          }
        </button>
      </div>
      {state === "error" && (
        <p className="text-[12px] text-red-500 font-body mt-2">{errMsg}</p>
      )}
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────
type EmailStatus = "processing" | "auto_sent" | "needs_review";
interface EmailItem {
  id: number; initials: string; from: string;
  subject: string; status: EmailStatus;
  responseTime?: string; aiDraft?: string;
}

// ─────────────────────────────────────────────────────────────────
// NAV
// ─────────────────────────────────────────────────────────────────
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const links = [
    { label: "How It Works", href: "#how-it-works" },
    { label: "Features",     href: "#features" },
    { label: "Pricing",      href: "#pricing" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "nav-clean" : "bg-white/80 backdrop-blur-md"}`}>
      <div className="max-w-6xl mx-auto px-6 h-[60px] flex items-center justify-between">
        <a href="#" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-r-ink rounded-lg flex items-center justify-center">
            <Mail size={14} className="text-white" />
          </div>
          <span className="font-display font-bold text-[17px] text-r-text tracking-tight">Resolved</span>
        </a>
        <div className="hidden md:flex items-center gap-7">
          {links.map(l => (
            <a key={l.href} href={l.href} className="text-[14px] text-r-muted hover:text-r-text transition-colors font-body">
              {l.label}
            </a>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-2.5">
          <a href="#waitlist" className="btn-primary text-[13px] px-4 py-2 font-body">Join waitlist</a>
        </div>
        <button className="md:hidden text-r-muted p-1" onClick={() => setOpen(!open)}>
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-r-border px-6 py-4 flex flex-col gap-4 shadow-lg">
          {links.map(l => (
            <a key={l.href} href={l.href} className="text-r-muted hover:text-r-text py-1 font-body text-sm" onClick={() => setOpen(false)}>
              {l.label}
            </a>
          ))}
          <a href="#waitlist" className="btn-primary py-2.5 text-sm font-body text-center block rounded-xl">Join waitlist</a>
        </div>
      )}
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────
// ANIMATED INBOX (hero)
// ─────────────────────────────────────────────────────────────────
const PROCESS_STEPS = [
  "Reading email...",
  "Searching order database...",
  "Found order #8841 · Shipped Feb 13",
  "Tracking: FedEx · ETA Feb 21",
  "Composing reply...",
];
const INIT_EMAILS: EmailItem[] = [
  { id: 1, initials: "SC", from: "sarah.chen@acmeco.io",  subject: "Where's my order #8841?",          status: "processing" },
  { id: 2, initials: "MR", from: "marcus@retailpro.com",  subject: "I want a full refund on 3 items",  status: "needs_review", aiDraft: "Hi Marcus, I've reviewed your recent order and can confirm we'll process the refund..." },
  { id: 3, initials: "LS", from: "lisa@shoppers.net",     subject: "What's your return window?",       status: "auto_sent",    responseTime: "1.2s" },
  { id: 4, initials: "DT", from: "dev@techcorp.com",      subject: "Do you ship to Canada?",           status: "auto_sent",    responseTime: "0.9s" },
];

function StatusTag({ status }: { status: EmailStatus }) {
  if (status === "processing") return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-code font-medium text-r-blue bg-r-blue-dim px-2 py-0.5 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-r-blue pulse-dot inline-block" />Replying now
    </span>
  );
  if (status === "auto_sent") return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-code font-medium text-r-green bg-r-green-dim px-2 py-0.5 rounded-full">
      <Check size={9} strokeWidth={3} />Sent
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-code font-medium text-r-orange bg-r-orange-dim px-2 py-0.5 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-r-orange inline-block" />Review
    </span>
  );
}

function EmailRow({ email, stepText, idx }: { email: EmailItem; stepText: string; idx: number }) {
  return (
    <div className="px-4 py-3 border-b border-r-border last:border-0" style={{ animation: `emailSlideIn 0.4s ease ${idx * 0.07}s both` }}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-r-elevated border border-r-border flex items-center justify-center text-[10px] font-semibold text-r-muted shrink-0 font-code mt-0.5">
          {email.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className="text-[12px] text-r-muted font-body truncate">{email.from}</p>
            <StatusTag status={email.status} />
          </div>
          <p className="text-[13px] text-r-text font-semibold font-body truncate leading-snug">{email.subject}</p>
          {email.status === "processing" && <p className="text-[11px] text-r-blue font-code mt-1.5">{stepText}</p>}
          {email.status === "auto_sent" && <p className="text-[11px] text-r-muted font-body mt-1">Replied automatically · {email.responseTime}</p>}
          {email.status === "needs_review" && email.aiDraft && (
            <div className="mt-2 space-y-1.5">
              <p className="text-[11px] text-r-muted font-body line-clamp-1 italic">"{email.aiDraft}"</p>
              <div className="flex gap-1.5">
                <button className="px-2.5 py-1 text-[11px] font-semibold font-body bg-r-ink text-white rounded-md hover:bg-r-text transition-colors">Approve & send</button>
                <button className="px-2.5 py-1 text-[11px] font-body border border-r-border text-r-muted rounded-md hover:border-r-border-hi transition-colors">Edit</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnimatedInbox() {
  const [emails, setEmails] = useState(INIT_EMAILS);
  const [step, setStep] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setStep(s => (s + 1) % PROCESS_STEPS.length), 1800);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      setEmails(prev => prev.map(e => e.id === 1 ? { ...e, status: "auto_sent" as const, responseTime: "2.1s" } : e));
    }, 6000);
    return () => clearTimeout(t);
  }, []);
  const active = emails.filter(e => e.status === "processing").length;
  return (
    <div className="bg-white rounded-2xl border border-r-border overflow-hidden" style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)" }}>
      <div className="h-0.5 bg-r-blue" />
      <div className="flex items-center justify-between px-4 py-3 border-b border-r-border bg-r-bg-alt">
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-r-muted" />
          <span className="font-body font-semibold text-[13px] text-r-text">Inbox</span>
          <span className="text-[11px] text-r-muted font-body">· support@acmeco.io</span>
        </div>
        {active > 0 && <span className="text-[10px] font-code text-r-blue bg-r-blue-dim px-2 py-0.5 rounded-full">{active} active</span>}
      </div>
      <div>{emails.map((e, i) => <EmailRow key={e.id} email={e} stepText={PROCESS_STEPS[step]} idx={i} />)}</div>
      <div className="px-4 py-2.5 bg-r-bg-alt border-t border-r-border flex items-center justify-between">
        <span className="text-[10px] font-code text-r-dim">Resolved AI · active</span>
        <div className="flex items-center gap-1 text-[10px] font-code text-r-green">
          <span className="w-1.5 h-1.5 rounded-full bg-r-green inline-block" />connected
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section className="relative isolate min-h-screen flex items-center pt-[60px] overflow-hidden bg-white">
      <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" style={{ zIndex: -1 }} />
      <div className="absolute -top-32 right-0 w-[700px] h-[500px] rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 65%)" }} />
      <div className="max-w-6xl mx-auto px-6 py-28 w-full grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-r-border bg-white text-r-muted text-[12px] font-code mb-8 shadow-sm">
            <Sparkles size={11} className="text-r-blue" />ORDER-AWARE SUPPORT AUTOMATION
          </div>
          <h1 className="font-display font-extrabold text-[52px] lg:text-[64px] text-r-ink leading-[1.06] tracking-tight mb-6">
            Support emails,<br /><span className="gradient-text">resolved.</span>
          </h1>
          <p className="text-r-muted text-[18px] leading-relaxed mb-4 max-w-lg font-body">
            Connect your Shopify store and share your policies, FAQs, and docs. We pull live order data,
            identify the exact product purchased, check real tracking status, and reply with accurate,
            order-specific answers — automatically.
          </p>
          <p className="text-[14px] text-r-dim font-body mb-10 max-w-md">
            Not another AI auto-reply tool. Product-aware, order-aware operational support.
          </p>
          <div className="max-w-md">
            <WaitlistForm source="hero" />
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 rounded-3xl pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(37,99,235,0.04) 0%, transparent 70%)" }} />
          <AnimatedInbox />
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────
// PROBLEM — editorial numbered rows, no cards
// ─────────────────────────────────────────────────────────────────
function ProblemSection() {
  const { ref, visible } = useScrollReveal();
  const rows = [
    {
      num: "01",
      headline: "Your team is a copy-paste machine.",
      body: "Order status. Return policies. Shipping times. They know every answer — they just have to keep typing it. Dozens of times a day. Every day.",
      stat: "~3 hrs", statNote: "lost daily to\nrepetitive replies",
    },
    {
      num: "02",
      headline: "Generic AI gives guesses. Customers want answers.",
      body: "Typical AI tools pull from a knowledge base and make things up. We connect directly to your Shopify store and fetch real order data — the exact SKU, the exact carrier, the exact delivery date — before every reply.",
      stat: "70%", statNote: "of support happens\nover email",
    },
    {
      num: "03",
      headline: "Volume grows. Headcount doesn't.",
      body: "Every new customer adds more tickets. You can't hire your way out of it. And slower replies erode the trust you worked hard to build.",
      stat: "4+ hrs", statNote: "industry avg first\nresponse time",
    },
  ];

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-5xl mx-auto px-6">
        <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} mb-14`}>
          <p className="text-[12px] font-code text-r-blue tracking-widest uppercase mb-4">The Problem</p>
          <h2 className="font-display font-bold text-[38px] lg:text-[52px] text-r-ink leading-tight max-w-xl">
            Email support is broken<br />and everyone knows it.
          </h2>
        </div>

        <div>
          {rows.map(({ num, headline, body, stat, statNote }, i) => (
            <div key={i}
                 className={`reveal reveal-delay-${i + 1} ${visible ? "is-visible" : ""}
                              flex items-start gap-6 lg:gap-10 py-10 border-t border-r-border group cursor-default`}>
              {/* Big decorative number */}
              <div className="font-display font-extrabold text-[64px] lg:text-[88px] leading-none
                              text-r-border group-hover:text-r-dim transition-colors duration-500
                              select-none w-20 lg:w-28 shrink-0 -mt-2">
                {num}
              </div>

              {/* Content */}
              <div className="flex-1 pt-1 min-w-0">
                <h3 className="font-display font-bold text-[20px] lg:text-[24px] text-r-ink mb-3 leading-snug
                               group-hover:text-r-blue transition-colors duration-300">
                  {headline}
                </h3>
                <p className="text-r-muted text-[15px] lg:text-[16px] leading-relaxed font-body max-w-lg">{body}</p>
              </div>

              {/* Stat */}
              <div className="hidden lg:flex flex-col items-end shrink-0 pt-1 gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                <span className="font-display font-extrabold text-[28px] text-r-ink leading-none">{stat}</span>
                <span className="text-[11px] text-r-dim font-body text-right whitespace-pre-line leading-snug">{statNote}</span>
              </div>
            </div>
          ))}
          <div className="border-t border-r-border" />
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// HOW IT WORKS — centered flow, no cards
// ─────────────────────────────────────────────────────────────────
function HowItWorksSection() {
  const { ref, visible } = useScrollReveal();
  const steps = [
    { num: "1", title: "Connect your store & share your knowledge", body: "One click to connect Shopify. Then add your return policy, FAQs, product docs — anything your support team would reference. We handle the rest.", tag: "Quick setup" },
    { num: "2", title: "We handle the inbox", body: "Every email is read, researched, and replied to automatically. Order lookups, policy questions, shipping updates — handled.", tag: "Avg. 1.8s response" },
    { num: "3", title: "You approve edge cases", body: "Refunds, complaints, anything sensitive — we draft the reply and notify you. One click to approve. You stay in control of what matters.", tag: "Full audit trail" },
  ];

  return (
    <section id="how-it-works" className="py-24 relative isolate overflow-hidden" style={{ background: "#0F0F10" }}>
      {/* Subtle grid */}
      <div className="absolute inset-0 dot-grid opacity-[0.07] pointer-events-none" style={{ zIndex: -1 }} />

      <div className="max-w-5xl mx-auto px-6 relative">
        <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} text-center mb-16`}>
          <p className="text-[12px] font-code text-r-blue tracking-widest uppercase mb-4">How It Works</p>
          <h2 className="font-display font-bold text-[38px] lg:text-[52px] leading-tight mb-4" style={{ color: "#F0F0F4" }}>
            One connection.<br />Everything automated.
          </h2>
          <p className="text-[17px] leading-relaxed font-body max-w-md mx-auto" style={{ color: "#6B6B7B" }}>
            Connect your Shopify store and share your policies in one step. We handle everything from there.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Vertical connector line */}
          <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px -translate-x-1/2"
               style={{ background: "linear-gradient(to bottom, transparent, rgba(37,99,235,0.3), rgba(37,99,235,0.3), transparent)" }} />

          <div className="space-y-0 md:space-y-0">
            {steps.map(({ num, title, body, tag }, i) => (
              <div key={i}
                   className={`reveal reveal-delay-${i + 1} ${visible ? "is-visible" : ""}
                                relative flex flex-col md:flex-row items-center gap-6 py-10
                                ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                {/* Text side */}
                <div className={`flex-1 ${i % 2 === 0 ? "md:text-right md:pr-16" : "md:text-left md:pl-16"}`}>
                  <h3 className="font-display font-bold text-[20px] lg:text-[22px] mb-2 leading-snug" style={{ color: "#F0F0F4" }}>
                    {title}
                  </h3>
                  <p className="text-[15px] leading-relaxed font-body mb-3" style={{ color: "#6B6B7B" }}>{body}</p>
                  <span className="inline-block text-[11px] font-code text-r-blue bg-r-blue/10 border border-r-blue/20 px-3 py-1 rounded-full">
                    {tag}
                  </span>
                </div>

                {/* Circle node */}
                <div className="relative z-10 shrink-0">
                  <div className="w-14 h-14 rounded-full bg-r-blue flex items-center justify-center
                                  font-display font-extrabold text-[18px] text-white
                                  shadow-[0_0_0_6px_rgba(37,99,235,0.15),0_0_0_12px_rgba(37,99,235,0.06)]">
                    {num}
                  </div>
                </div>

                {/* Empty side for balance */}
                <div className="flex-1 hidden md:block" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// FEATURES — interactive selector panel
// ─────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: <Zap size={22} className="text-r-blue" />,
    title: "Instant auto-replies",
    short: "Under 2 seconds",
    body: "Clear, answerable questions get a reply sent in under 2 seconds. No queue, no delay, no human needed. The customer gets an answer before most teams have even opened their inbox.",
    detail: { label: "Avg response time", value: "1.8s" },
    preview: (
      <div className="space-y-2">
        {[
          { from: "lisa@shoppers.net", subject: "What's your return window?", time: "1.2s", done: true },
          { from: "dev@techcorp.com",   subject: "Do you ship to Canada?",     time: "0.9s", done: true },
          { from: "jay@outdoors.io",    subject: "Is the XL still in stock?",  time: "1.7s", done: true },
        ].map((e, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b border-r-border last:border-0">
            <div className="w-6 h-6 rounded-full bg-r-green-dim border border-r-green/30 flex items-center justify-center shrink-0">
              <Check size={10} strokeWidth={3} className="text-r-green" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-body text-r-text font-medium truncate">{e.subject}</p>
              <p className="text-[11px] font-code text-r-dim">{e.from}</p>
            </div>
            <span className="text-[10px] font-code text-r-green bg-r-green-dim px-2 py-0.5 rounded-full shrink-0">{e.time}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: <ShoppingBag size={22} className="text-r-blue" />,
    title: "Built-in order lookup",
    short: "Live Shopify data",
    body: "We connect directly to your Shopify store and pull live data before every reply. No generic answers. No AI filler. The exact order, exact SKU, exact carrier, exact ETA — every time.",
    detail: { label: "Data sources", value: "Shopify · FedEx · USPS" },
    preview: (
      <div className="rounded-xl border border-r-border overflow-hidden">
        <div className="bg-r-bg-alt border-b border-r-border px-3 py-2 flex items-center gap-2">
          <ShoppingBag size={11} className="text-r-muted" />
          <span className="text-[11px] font-code text-r-muted">Order lookup · #4521</span>
        </div>
        <div className="divide-y divide-r-border">
          {[
            { k: "Customer", v: "Sarah Chen" },
            { k: "Status", v: "Shipped", badge: true },
            { k: "Carrier", v: "FedEx" },
            { k: "ETA", v: "Feb 21, 2026" },
          ].map(({ k, v, badge }) => (
            <div key={k} className="flex items-center gap-3 px-3 py-2">
              <span className="text-[11px] font-code text-r-dim w-16 shrink-0">{k}</span>
              <span className="text-[12px] font-body text-r-text font-medium">{v}</span>
              {badge && <span className="ml-auto text-[10px] font-code text-r-blue bg-r-blue-dim px-2 py-0.5 rounded-full">live</span>}
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    icon: <BookOpen size={22} className="text-r-blue" />,
    title: "Learns your business",
    short: "Knows your policies",
    body: "Feed us your FAQs, return policies, product pages, and docs. We build a knowledge model that answers in your voice — so replies feel like they came from your best support rep.",
    detail: { label: "Sources ingested", value: "Docs · FAQs · Product catalog" },
    preview: (
      <div className="space-y-2">
        {[
          { src: "Return Policy.pdf", status: "Indexed", n: "12 policies" },
          { src: "Product Catalog",   status: "Indexed", n: "847 SKUs" },
          { src: "FAQ / Help Center", status: "Indexed", n: "94 answers" },
        ].map(({ src, status, n }) => (
          <div key={src} className="flex items-center gap-3 py-2 border-b border-r-border last:border-0">
            <div className="w-6 h-6 rounded-full bg-r-blue-dim border border-r-blue/20 flex items-center justify-center shrink-0">
              <BookOpen size={10} className="text-r-blue" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-body text-r-text font-medium">{src}</p>
              <p className="text-[11px] font-code text-r-dim">{n}</p>
            </div>
            <span className="text-[10px] font-code text-r-green bg-r-green-dim px-2 py-0.5 rounded-full shrink-0">{status}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: <Shield size={22} className="text-r-blue" />,
    title: "Human review queue",
    short: "You stay in control",
    body: "Anything sensitive, unusual, or high-value is routed to you with a fully drafted reply. One click to approve and send, or edit it first. You're never out of the loop on what matters.",
    detail: { label: "Avg review time", value: "< 45 seconds" },
    preview: (
      <div className="rounded-xl border border-r-orange/30 bg-r-orange-dim overflow-hidden">
        <div className="px-3 py-2.5 border-b border-r-orange/20 flex items-center gap-2">
          <Shield size={11} className="text-r-orange" />
          <span className="text-[11px] font-code text-r-orange">Needs your review</span>
        </div>
        <div className="px-3 py-3">
          <p className="text-[12px] font-semibold text-r-text font-body mb-1">Re: I want a full refund</p>
          <p className="text-[12px] text-r-muted font-body mb-3 leading-relaxed italic line-clamp-2">"Hi Marcus, I've reviewed your order and can confirm we'll process the full refund within 3-5 business days..."</p>
          <div className="flex gap-2">
            <button className="px-3 py-1.5 text-[11px] font-semibold font-body bg-r-ink text-white rounded-lg">Approve & send</button>
            <button className="px-3 py-1.5 text-[11px] font-body border border-r-border text-r-muted rounded-lg bg-white">Edit first</button>
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <Lock size={22} className="text-r-blue" />,
    title: "Sends from your domain",
    short: "Your email, seamlessly",
    body: "Customers receive replies from your support address, not ours. Simple one-time setup included. To Sarah, it's just a fast, accurate reply from your team — she never knows Resolved is involved.",
    detail: { label: "Setup time", value: "< 10 minutes" },
    preview: (
      <div className="rounded-xl border border-r-border overflow-hidden">
        <div className="bg-r-bg-alt border-b border-r-border px-3 py-2 flex items-center gap-2">
          <Mail size={11} className="text-r-muted" />
          <span className="text-[11px] font-code text-r-muted">sarah's inbox</span>
        </div>
        <div className="px-3 py-3">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[12px] font-semibold text-r-text font-body">support@shopco.io</span>
            <span className="text-[10px] font-code text-r-blue bg-r-blue-dim px-2 py-0.5 rounded-full">your domain ✓</span>
          </div>
          <p className="text-[12px] font-semibold text-r-text font-body">Re: Where is my order?</p>
          <p className="text-[11px] text-r-dim font-body mt-1 line-clamp-2">Hi Sarah, Your order #4521 shipped on February 14 via FedEx. Estimated delivery...</p>
        </div>
      </div>
    ),
  },
  {
    icon: <RefreshCw size={22} className="text-r-blue" />,
    title: "Gets smarter over time",
    short: "Self-improving model",
    body: "Every handled email teaches the system. Reply quality improves, edge case coverage expands, and your auto-resolution rate climbs — all without any manual retraining from you.",
    detail: { label: "Improvement", value: "Auto-retrains weekly" },
    preview: (
      <div className="space-y-3">
        {[
          { label: "Auto-resolution rate", before: 78, after: 94 },
          { label: "Avg accuracy score",   before: 82, after: 97 },
        ].map(({ label, before, after }) => (
          <div key={label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-body text-r-muted">{label}</span>
              <span className="text-[11px] font-code text-r-green">+{after - before}%</span>
            </div>
            <div className="relative h-1.5 bg-r-border rounded-full overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-r-blue rounded-full" style={{ width: `${after}%` }} />
              <div className="absolute inset-y-0 left-0 bg-r-dim rounded-full opacity-40" style={{ width: `${before}%` }} />
            </div>
            <div className="flex justify-between mt-0.5">
              <span className="text-[10px] font-code text-r-dim">Week 1 · {before}%</span>
              <span className="text-[10px] font-code text-r-green">Now · {after}%</span>
            </div>
          </div>
        ))}
      </div>
    ),
  },
];

function FeaturesSection() {
  const { ref, visible } = useScrollReveal();
  const [active, setActive] = useState(0);
  const [panelKey, setPanelKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function selectFeature(i: number) {
    setActive(i);
    setPanelKey(k => k + 1);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive(a => { const next = (a + 1) % FEATURES.length; setPanelKey(k => k + 1); return next; });
    }, 4000);
  }

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActive(a => { const next = (a + 1) % FEATURES.length; setPanelKey(k => k + 1); return next; });
    }, 4000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const f = FEATURES[active];

  return (
    <section id="features" className="py-24 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} mb-14`}>
          <p className="text-[12px] font-code text-r-blue tracking-widest uppercase mb-4">What We Handle</p>
          <h2 className="font-display font-bold text-[38px] lg:text-[52px] text-r-ink leading-tight max-w-xl">
            Built to replace the work,<br />not just assist it.
          </h2>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8 items-start">
          {/* Left: feature list */}
          <div className="space-y-1">
            {FEATURES.map((feat, i) => (
              <button key={i} onClick={() => selectFeature(i)}
                className={`w-full text-left px-4 py-3.5 rounded-xl transition-all duration-200 group flex items-center gap-3
                  ${active === i
                    ? "bg-r-blue text-white shadow-[0_4px_16px_rgba(37,99,235,0.25)]"
                    : "hover:bg-r-bg-alt text-r-muted hover:text-r-text"}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors
                  ${active === i ? "bg-white/15" : "bg-r-blue-dim"}`}>
                  <span className={active === i ? "[&_*]:text-white" : ""}>{feat.icon}</span>
                </div>
                <div className="min-w-0">
                  <p className={`text-[13px] font-semibold font-body leading-snug ${active === i ? "text-white" : "text-r-text"}`}>
                    {feat.title}
                  </p>
                  <p className={`text-[11px] font-code leading-none mt-0.5 ${active === i ? "text-white/70" : "text-r-dim"}`}>
                    {feat.short}
                  </p>
                </div>
                <ChevronRight size={14} className={`ml-auto shrink-0 transition-transform ${active === i ? "text-white/80 translate-x-0" : "text-r-dim -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`} />
              </button>
            ))}
          </div>

          {/* Right: detail panel */}
          <div key={panelKey} className="rounded-2xl border border-r-border bg-white overflow-hidden"
               style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04)", animation: "sceneIn 0.35s cubic-bezier(0.16,1,0.3,1) both" }}>
            {/* Header */}
            <div className="border-b border-r-border px-8 py-6 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-r-blue-dim border border-r-blue/20 flex items-center justify-center shrink-0">
                {f.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-[20px] text-r-ink mb-1 leading-snug">{f.title}</h3>
                <p className="text-r-muted text-[15px] font-body leading-relaxed">{f.body}</p>
              </div>
              <div className="shrink-0 text-right hidden sm:block">
                <p className="text-[11px] font-code text-r-dim uppercase tracking-wide mb-0.5">{f.detail.label}</p>
                <p className="text-[13px] font-code text-r-blue font-medium">{f.detail.value}</p>
              </div>
            </div>

            {/* Preview */}
            <div className="px-8 py-6">
              <p className="text-[11px] font-code text-r-dim uppercase tracking-widest mb-4">Preview</p>
              {f.preview}
            </div>

            {/* Progress dots */}
            <div className="border-t border-r-border px-8 py-3 flex items-center gap-1.5">
              {FEATURES.map((_, i) => (
                <button key={i} onClick={() => selectFeature(i)}
                  className={`h-1 rounded-full transition-all duration-300 ${i === active ? "w-6 bg-r-blue" : "w-1.5 bg-r-border hover:bg-r-dim"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// STORYBOARD SCENES
// ─────────────────────────────────────────────────────────────────

// Shared email header row
function EmailField({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-r-border last:border-0">
      <span className="text-[11px] font-code text-r-dim w-14 shrink-0 uppercase tracking-wide">{label}</span>
      <span className={`text-[13px] font-body ${highlight ? "font-semibold text-r-blue" : "text-r-text"}`}>{value}</span>
      {highlight && <span className="ml-auto text-[10px] font-code text-r-blue bg-r-blue-dim px-2 py-0.5 rounded-full">your domain</span>}
    </div>
  );
}

// Scene 1: Customer writes an email
function Scene1() {
  const BODY = "Hi,\n\nI placed an order last Thursday (order #4521) but haven't received any shipping info. Can you let me know when it'll arrive?\n\nThanks,\nSarah";
  const typed = useTypewriter(BODY, 20, 600);
  const showSend = useShow(4200);

  return (
    <div className="p-8 h-full flex flex-col lg:flex-row gap-8 items-start">
      {/* Left: context */}
      <div className="lg:w-48 shrink-0">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-code text-r-blue bg-r-blue-dim px-2.5 py-1 rounded-full mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-r-blue inline-block" />Scene 1 of 5
        </div>
        <h3 className="font-display font-bold text-[18px] text-r-ink mb-2 leading-snug">Customer sends an email</h3>
        <p className="text-[13px] text-r-muted font-body leading-relaxed">
          Sarah needs help with her order. She emails your support address — just like she always would.
        </p>
        <div className="mt-4 text-[12px] font-code text-r-dim">sarah.chen@gmail.com → support@shopco.io</div>
      </div>

      {/* Right: email compose mockup */}
      <div className="flex-1 rounded-xl border border-r-border overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        {/* Gmail-style toolbar */}
        <div className="bg-r-bg-alt border-b border-r-border px-4 py-2.5 flex items-center justify-between">
          <span className="text-[13px] font-semibold text-r-text font-body">New Message</span>
          <div className="flex gap-3 text-r-dim">
            <span className="text-[18px] leading-none cursor-pointer hover:text-r-text">−</span>
            <span className="text-[14px] leading-none cursor-pointer hover:text-r-text">⤢</span>
            <X size={15} className="cursor-pointer hover:text-r-text" />
          </div>
        </div>
        <div className="px-4">
          <EmailField label="From" value="sarah.chen@gmail.com" />
          <EmailField label="To" value="support@shopco.io" />
          <EmailField label="Subject" value="Where is my order?" />
        </div>
        <div className="px-4 pt-3 pb-4 min-h-[140px]">
          <pre className="font-body text-[14px] text-r-text leading-relaxed whitespace-pre-wrap">
            {typed}<span className="inline-block w-0.5 h-4 bg-r-text align-middle" style={{ animation: "blink 1s step-end infinite" }} />
          </pre>
        </div>
        {showSend && (
          <div className="px-4 pb-4 flex items-center gap-3" style={{ animation: "rowAppear 0.4s ease both" }}>
            <button className="btn-primary px-5 py-2 text-[13px] font-body flex items-center gap-2">
              <Mail size={13} /> Send
            </button>
            <span className="text-[11px] font-code text-r-muted">Sending to support@shopco.io</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Scene 2: Resolved reads the email
function Scene2() {
  const showH1 = useShow(400);   // order #4521 highlight
  const showH2 = useShow(1000);  // last Thursday highlight
  const showH3 = useShow(1600);  // when it'll arrive highlight
  const showTag1 = useShow(2300);
  const showTag2 = useShow(2700);
  const showTag3 = useShow(3100);

  const hl = (text: string, color: string, show: boolean) => (
    <span style={{
      background: show ? color : "transparent",
      borderRadius: "3px",
      padding: "1px 3px",
      transition: "background 0.5s ease",
    }}>{text}</span>
  );

  return (
    <div className="p-8 h-full flex flex-col lg:flex-row gap-8 items-start">
      <div className="lg:w-48 shrink-0">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-code text-r-blue bg-r-blue-dim px-2.5 py-1 rounded-full mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-r-blue pulse-dot inline-block" />Scene 2 of 5
        </div>
        <h3 className="font-display font-bold text-[18px] text-r-ink mb-2 leading-snug">Resolved reads it</h3>
        <p className="text-[13px] text-r-muted font-body leading-relaxed">
          The AI scans the email and extracts what it needs to know — intent, order reference, and urgency.
        </p>
        <div className="mt-5 space-y-2">
          {[
            { label: "Order ref", val: "#4521",           color: "#DCFCE7", show: showH1 },
            { label: "Timeframe", val: "last Thursday",   color: "#DBEAFE", show: showH2 },
            { label: "Intent",    val: "shipping update", color: "#EDE9FE", show: showH3 },
          ].map(({ label, val, color, show }) => (
            <div key={label} className="flex items-center gap-2 transition-opacity duration-300" style={{ opacity: show ? 1 : 0.2 }}>
              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: show ? "#2563EB" : "#D1D5DB" }} />
              <span className="text-[11px] font-code text-r-dim">{label}:</span>
              <span className="text-[11px] font-body text-r-text font-medium">{val}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 rounded-xl border border-r-border overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div className="bg-r-bg-alt border-b border-r-border px-4 py-2.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-r-blue pulse-dot inline-block" />
          <span className="text-[12px] font-code text-r-blue">Resolved · reading</span>
        </div>
        <div className="px-4">
          <EmailField label="From" value="sarah.chen@gmail.com" />
          <EmailField label="To" value="support@shopco.io" />
          <EmailField label="Subject" value="Where is my order?" />
        </div>
        <div className="px-4 py-4 font-body text-[14px] text-r-text leading-relaxed">
          <p>Hi,</p>
          <p className="mt-2">
            I placed an order {hl("last Thursday", "#DBEAFE", showH2)} and haven't received any shipping info.
            My {hl("order #4521", "#DCFCE7", showH1)} — can you let me know {hl("when it'll arrive", "#EDE9FE", showH3)}?
          </p>
          <p className="mt-2">Thanks,<br />Sarah</p>
        </div>
        {showTag1 && (
          <div className="px-4 pb-4 flex flex-wrap gap-2" style={{ animation: "rowAppear 0.3s ease both" }}>
            {showTag1 && <span className="text-[11px] font-code bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-full">Order: #4521</span>}
            {showTag2 && <span className="text-[11px] font-code bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full">Intent: shipping inquiry</span>}
            {showTag3 && <span className="text-[11px] font-code bg-violet-50 text-violet-700 border border-violet-200 px-2.5 py-1 rounded-full">Urgency: normal</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// Scene 3: Data lookup
function Scene3() {
  const rows = [
    { label: "Customer",   value: "Sarah Chen",        delay: 400 },
    { label: "Order",      value: "#4521",             delay: 800 },
    { label: "Status",     value: "Shipped",           delay: 1200, badge: "shipped" },
    { label: "Date",       value: "February 14, 2026", delay: 1600 },
    { label: "Carrier",    value: "FedEx",             delay: 2000 },
    { label: "Tracking",   value: "1Z999AA01234567",   delay: 2400 },
    { label: "Delivery",   value: "February 21, 2026", delay: 2800, badge: "eta" },
  ];
  const shown = rows.map(r => useShow(r.delay));
  const showDone = useShow(3400);

  return (
    <div className="p-8 h-full flex flex-col lg:flex-row gap-8 items-start">
      <div className="lg:w-48 shrink-0">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-code text-r-blue bg-r-blue-dim px-2.5 py-1 rounded-full mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-r-blue pulse-dot inline-block" />Scene 3 of 5
        </div>
        <h3 className="font-display font-bold text-[18px] text-r-ink mb-2 leading-snug">Finding the answer</h3>
        <p className="text-[13px] text-r-muted font-body leading-relaxed">
          Resolved queries your store for order #4521 and pulls everything needed to write an accurate reply.
        </p>
        {showDone && (
          <div className="mt-4 flex items-center gap-2 text-r-green text-[12px] font-code" style={{ animation: "rowAppear 0.4s ease both" }}>
            <Check size={14} strokeWidth={2.5} />All data found
          </div>
        )}
      </div>

      <div className="flex-1 rounded-xl border border-r-border overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div className="bg-r-bg-alt border-b border-r-border px-4 py-2.5 flex items-center gap-2">
          <ShoppingBag size={13} className="text-r-muted" />
          <span className="text-[12px] font-code text-r-muted">Querying order database for #4521</span>
        </div>
        <div className="divide-y divide-r-border">
          {rows.map(({ label, value, badge }, i) => (
            shown[i] && (
              <div key={label} className="flex items-center gap-4 px-4 py-2.5" style={{ animation: "rowAppear 0.35s ease both" }}>
                <div className="w-5 h-5 rounded-full bg-r-green-dim border border-r-green/30 flex items-center justify-center shrink-0">
                  <Check size={10} strokeWidth={3} className="text-r-green" />
                </div>
                <span className="text-[12px] font-code text-r-dim w-20 shrink-0">{label}</span>
                <span className="text-[13px] font-body text-r-text font-medium">{value}</span>
                {badge === "shipped" && <span className="ml-auto text-[10px] font-code bg-r-blue-dim text-r-blue px-2 py-0.5 rounded-full">shipped</span>}
                {badge === "eta" && <span className="ml-auto text-[10px] font-code bg-r-green-dim text-r-green px-2 py-0.5 rounded-full">ETA confirmed</span>}
              </div>
            )
          ))}
        </div>
      </div>
    </div>
  );
}

// Scene 4: Reply drafted
function Scene4() {
  const BODY = "Hi Sarah,\n\nYour order #4521 shipped on February 14 via FedEx.\n\nEstimated delivery: Friday, February 21\nTrack here: fedex.com/track?n=1Z999AA01234567\n\nLet us know if you need anything else!\n\n— Shop Co. Support";
  const typed = useTypewriter(BODY, 18, 500);
  const showReady = useShow(4800);

  return (
    <div className="p-8 h-full flex flex-col lg:flex-row gap-8 items-start">
      <div className="lg:w-48 shrink-0">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-code text-r-blue bg-r-blue-dim px-2.5 py-1 rounded-full mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-r-blue pulse-dot inline-block" />Scene 4 of 5
        </div>
        <h3 className="font-display font-bold text-[18px] text-r-ink mb-2 leading-snug">Reply drafted</h3>
        <p className="text-[13px] text-r-muted font-body leading-relaxed">
          Using the order data it just retrieved, the AI writes a complete, accurate reply — in your voice.
        </p>
        {showReady && (
          <div className="mt-4 text-[12px] font-code text-r-green flex items-center gap-1.5" style={{ animation: "rowAppear 0.4s ease both" }}>
            <Check size={13} strokeWidth={2.5} />Ready to send
          </div>
        )}
      </div>

      <div className="flex-1 rounded-xl border border-r-border overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div className="bg-r-bg-alt border-b border-r-border px-4 py-2.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-r-blue pulse-dot inline-block" />
          <span className="text-[12px] font-code text-r-blue">Composing reply...</span>
        </div>
        <div className="px-4">
          <EmailField label="From" value="support@shopco.io" highlight />
          <EmailField label="To" value="sarah.chen@gmail.com" />
          <EmailField label="Subject" value="Re: Where is my order?" />
        </div>
        <div className="px-4 py-4 min-h-[150px]">
          <pre className="font-body text-[14px] text-r-text leading-relaxed whitespace-pre-wrap">
            {typed}<span className="inline-block w-0.5 h-4 bg-r-text align-middle" style={{ animation: "blink 1s step-end infinite" }} />
          </pre>
        </div>
      </div>
    </div>
  );
}

// Scene 5: Sent
function Scene5() {
  const showCheck  = useShow(200);
  const showFrom   = useShow(900);
  const showEmail  = useShow(1400);
  const showTimer  = useShow(2100);
  const showLabel  = useShow(2800);

  return (
    <div className="p-8 h-full flex flex-col items-center justify-center text-center">
      {showCheck && (
        <div className="w-20 h-20 rounded-full bg-r-green-dim border-2 border-r-green/30 flex items-center justify-center mb-6"
             style={{ animation: "checkPop 0.6s cubic-bezier(0.34,1.56,0.64,1) both" }}>
          <Check size={36} strokeWidth={2} className="text-r-green" />
        </div>
      )}

      {showFrom && (
        <div className="mb-2" style={{ animation: "rowAppear 0.4s ease both" }}>
          <h3 className="font-display font-bold text-[26px] text-r-ink">Sent.</h3>
          <p className="text-[15px] text-r-muted font-body mt-1">
            Reply delivered from <span className="font-semibold text-r-blue">support@shopco.io</span>
          </p>
          <p className="text-[13px] text-r-dim font-body">Sarah receives it as a normal email from your company.</p>
        </div>
      )}

      {showEmail && (
        <div className="mt-4 w-full max-w-sm rounded-xl border border-r-border overflow-hidden text-left"
             style={{ animation: "rowAppear 0.4s ease both", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div className="h-0.5 bg-r-green" />
          <div className="bg-r-bg-alt border-b border-r-border px-4 py-2 flex items-center gap-2">
            <Mail size={12} className="text-r-muted" />
            <span className="text-[11px] font-code text-r-muted">sarah's inbox</span>
          </div>
          <div className="px-4 py-3 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-semibold text-r-text font-body">support@shopco.io</span>
              <span className="text-[11px] text-r-dim font-code">just now</span>
            </div>
            <p className="text-[13px] font-semibold text-r-text font-body">Re: Where is my order?</p>
            <p className="text-[12px] text-r-muted font-body line-clamp-2">Hi Sarah, Your order #4521 shipped on February 14 via FedEx. Estimated delivery: Friday, Feb 21...</p>
          </div>
        </div>
      )}

      {showTimer && (
        <div className="mt-5 flex items-center gap-6" style={{ animation: "rowAppear 0.4s ease both" }}>
          <div className="text-center">
            <div className="font-display font-extrabold text-[32px] text-r-ink">2.3s</div>
            <div className="text-[11px] text-r-dim font-code uppercase tracking-wide">total time</div>
          </div>
          <div className="w-px h-10 bg-r-border" />
          <div className="text-center">
            <div className="font-display font-extrabold text-[32px] text-r-ink">0</div>
            <div className="text-[11px] text-r-dim font-code uppercase tracking-wide">humans involved</div>
          </div>
          {showLabel && (
            <>
              <div className="w-px h-10 bg-r-border" />
              <div className="text-center">
                <div className="font-display font-extrabold text-[32px] text-r-ink">100%</div>
                <div className="text-[11px] text-r-dim font-code uppercase tracking-wide">accurate</div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// STORYBOARD SECTION
// ─────────────────────────────────────────────────────────────────
const SCENE_DURATION = 5200;
const STEP_META = [
  { label: "Email arrives" },
  { label: "AI reads it" },
  { label: "Finds answer" },
  { label: "Drafts reply" },
  { label: "Delivered" },
];
const SCENE_COMPONENTS = [Scene1, Scene2, Scene3, Scene4, Scene5];

function StoryboardSection() {
  const { ref, visible } = useScrollReveal(0.15);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [sceneKey, setSceneKey] = useState(0); // forces remount
  const [progressKey, setProgressKey] = useState(0);
  const [started, setStarted] = useState(false);

  // Start auto-play when section scrolls into view
  useEffect(() => {
    if (visible && !started) { setStarted(true); setPlaying(true); }
  }, [visible]);

  // Auto-advance
  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => {
      setCurrent(c => {
        if (c < STEP_META.length - 1) {
          const next = c + 1;
          setSceneKey(k => k + 1);
          setProgressKey(k => k + 1);
          return next;
        }
        setPlaying(false);
        return c;
      });
    }, SCENE_DURATION);
    return () => clearTimeout(t);
  }, [current, playing]);

  function goTo(idx: number) {
    if (idx === current) return;
    setCurrent(idx);
    setSceneKey(k => k + 1);
    setProgressKey(k => k + 1);
    setPlaying(true);
  }
  function prev() { if (current > 0) goTo(current - 1); }
  function next() { if (current < STEP_META.length - 1) goTo(current + 1); else { goTo(0); } }
  function togglePlay() {
    if (!playing && current === STEP_META.length - 1) { goTo(0); }
    else setPlaying(p => !p);
  }

  const SceneComp = SCENE_COMPONENTS[current];

  return (
    <section className="py-24 bg-r-bg-alt relative isolate overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" style={{ zIndex: -1 }} />
      <div className="max-w-5xl mx-auto px-6 relative">
        {/* Header */}
        <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} text-center mb-12`}>
          <p className="text-[12px] font-code text-r-blue tracking-widest uppercase mb-4">In Action</p>
          <h2 className="font-display font-bold text-[38px] lg:text-[48px] text-r-ink mb-4 leading-tight">
            From inbox to reply<br />in under 3 seconds
          </h2>
          <p className="text-r-muted max-w-lg mx-auto font-body text-[17px] leading-relaxed">
            Follow a real support email through every step — from the moment Sarah hits send to when the reply lands back in her inbox.
          </p>
        </div>

        {/* Step indicators */}
        <div className={`reveal reveal-delay-2 ${visible ? "is-visible" : ""} flex items-center mb-6`}>
          {STEP_META.map(({ label }, i) => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <button onClick={() => goTo(i)} className="flex flex-col items-center gap-1.5 group">
                <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-[12px] font-bold font-code transition-all duration-300
                  ${i === current  ? "border-r-blue bg-r-blue text-white shadow-[0_0_0_4px_rgba(37,99,235,0.15)]"
                  : i < current    ? "border-r-blue bg-r-blue-dim text-r-blue"
                                   : "border-r-border bg-white text-r-dim"}`}>
                  {i < current ? <Check size={13} strokeWidth={2.5} /> : i + 1}
                </div>
                <span className={`text-[11px] font-body whitespace-nowrap transition-colors hidden sm:block
                  ${i === current ? "text-r-ink font-semibold" : "text-r-dim"}`}>
                  {label}
                </span>
              </button>
              {i < STEP_META.length - 1 && (
                <div className="flex-1 mx-2 h-0.5 rounded-full overflow-hidden bg-r-border" style={{ minWidth: "20px" }}>
                  <div className={`h-full rounded-full bg-r-blue transition-all duration-500 ${i < current ? "w-full" : "w-0"}`} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="h-1 rounded-full bg-r-border mb-4 overflow-hidden">
          {playing && (
            <div key={`pb-${progressKey}`} className="h-full bg-r-blue rounded-full"
                 style={{ transformOrigin: "left", animation: `progressFill ${SCENE_DURATION}ms linear forwards` }} />
          )}
          {!playing && (
            <div className="h-full bg-r-blue rounded-full" style={{ width: playing ? undefined : current === STEP_META.length - 1 ? "100%" : "0%" }} />
          )}
        </div>

        {/* Scene stage */}
        <div className={`reveal reveal-delay-3 ${visible ? "is-visible" : ""}`}>
          <div className="bg-white rounded-2xl border border-r-border overflow-hidden"
               style={{ minHeight: "520px", boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)" }}>
            <div key={`scene-${sceneKey}`} style={{ animation: "sceneIn 0.4s cubic-bezier(0.16,1,0.3,1) both" }}>
              <SceneComp />
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between mt-4">
          <button onClick={prev} disabled={current === 0}
                  className="flex items-center gap-1.5 text-[13px] font-body text-r-muted hover:text-r-text transition-colors disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1">
            <ChevronLeft size={16} /> Prev
          </button>

          <div className="flex items-center gap-3">
            <button onClick={togglePlay}
                    className="flex items-center gap-1.5 text-[12px] font-code text-r-muted hover:text-r-text transition-colors px-3 py-1.5 rounded-lg border border-r-border hover:border-r-border-hi bg-white">
              {playing ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Play</>}
            </button>
            <span className="text-[12px] font-code text-r-dim">Step {current + 1} of {STEP_META.length}</span>
          </div>

          <button onClick={next}
                  className="flex items-center gap-1.5 text-[13px] font-body text-r-muted hover:text-r-text transition-colors px-2 py-1">
            {current === STEP_META.length - 1 ? "Restart" : "Next"} <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────
// PRICING
// ─────────────────────────────────────────────────────────────────
function PricingSection() {
  const { ref, visible } = useScrollReveal();
  const [annual, setAnnual] = useState(false);
  const tiers = [
    {
      name: "Email", monthly: 129, ann: 103,
      desc: "Automated email support for small and growing teams.",
      highlight: false,
      features: ["Up to 1,000 emails / month","Live Shopify order + SKU lookup","Human review queue for edge cases","Sends from your domain","Knowledge base sync","Email support","14-day free trial"],
      cta: "Start free trial",
    },
    {
      name: "Email + Social", monthly: 199, ann: 159,
      desc: "Everything in Email, plus social inbox management.",
      highlight: true,
      features: ["Up to 3,000 emails / month","Everything in Email","Social inbox handling","Instagram, Facebook, X DMs","Team approval workflows","Analytics dashboard","Priority support","14-day free trial"],
      cta: "Start free trial",
    },
    {
      name: "High Volume", monthly: 349, ann: 279,
      desc: "For teams with serious support volume and custom needs.",
      highlight: false,
      features: ["Unlimited emails","Everything in Email + Social","Custom knowledge integrations","Dedicated success manager","99.9% uptime SLA","Audit logs & exports","White-glove onboarding"],
      cta: "Talk to us",
    },
  ];
  return (
    <section id="pricing" className="py-24 bg-r-bg-alt">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} text-center mb-12`}>
          <p className="text-[12px] font-code text-r-blue tracking-widest uppercase mb-4">Pricing</p>
          <h2 className="font-display font-bold text-[38px] lg:text-[48px] text-r-ink mb-4 leading-tight">Simple pricing. No surprises.</h2>
          <p className="text-r-muted max-w-md mx-auto font-body text-[17px] mb-8">All plans start with a 14-day free trial. Cancel anytime.</p>
          <div className="inline-flex items-center p-1 rounded-xl bg-white border border-r-border shadow-sm">
            <button onClick={() => setAnnual(false)} className={`px-4 py-1.5 rounded-lg text-[13px] font-body transition-all ${!annual ? "bg-r-ink text-white font-semibold" : "text-r-muted"}`}>Monthly</button>
            <button onClick={() => setAnnual(true)}  className={`px-4 py-1.5 rounded-lg text-[13px] font-body transition-all ${annual  ? "bg-r-ink text-white font-semibold" : "text-r-muted"}`}>
              Annual <span className="text-r-green text-[11px] ml-1">Save 20%</span>
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {tiers.map(({ name, monthly, ann, desc, highlight, features, cta }, i) => (
            <div key={i} className={`reveal reveal-delay-${i + 1} ${visible ? "is-visible" : ""} rounded-2xl border p-6 flex flex-col transition-all duration-300
              ${highlight ? "border-r-blue bg-white card-shadow shadow-[0_4px_24px_rgba(37,99,235,0.10)]" : "border-r-border bg-white card-shadow"}`}>
              {highlight && <span className="text-[10px] font-code text-r-blue bg-r-blue-dim px-2.5 py-0.5 rounded-full w-fit mb-4 tracking-widest">MOST POPULAR</span>}
              <h3 className="font-display font-bold text-[20px] text-r-ink mb-1">{name}</h3>
              <p className="text-r-muted text-[13px] font-body mb-5">{desc}</p>
              <div className="mb-6">
                <span className="font-display font-extrabold text-[36px] text-r-ink">${annual ? ann : monthly}</span>
                <span className="text-r-muted text-[13px] font-body ml-1">/month</span>
                {annual && <span className="ml-2 text-[11px] font-code text-r-green">billed annually</span>}
              </div>
              <ul className="space-y-2.5 flex-1 mb-8">
                {features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-[13px] font-body text-r-muted">
                    <Check size={13} strokeWidth={2.5} className={`mt-0.5 shrink-0 ${highlight ? "text-r-blue" : "text-r-green"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <a href="#waitlist" className={`w-full py-3 rounded-xl text-[13px] font-semibold font-body transition-all text-center block ${highlight ? "btn-primary" : "btn-secondary"}`}>Join waitlist</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// WAITLIST SECTION
// ─────────────────────────────────────────────────────────────────
function WaitlistSection() {
  const { ref, visible } = useScrollReveal();
  return (
    <section id="waitlist" className="py-28 bg-white relative isolate overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-70 pointer-events-none" style={{ zIndex: -1 }} />
      {/* Blue glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)" }} />
      <div ref={ref} className="max-w-xl mx-auto px-6 text-center relative">
        <div className={`reveal ${visible ? "is-visible" : ""}`}>
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-r-ink mb-8 floating">
            <Mail size={24} className="text-white" />
          </div>
          <h2 className="font-display font-extrabold text-[40px] lg:text-[54px] text-r-ink mb-5 leading-[1.08]">
            Be first in line<br />when we launch.
          </h2>
          <p className="text-r-muted text-[17px] font-body mb-10 leading-relaxed">
            Resolved is coming soon. Drop your email and we'll reach out
            the moment you can connect your first Shopify store.
          </p>
          <WaitlistForm source="cta" large />
          <p className="mt-5 text-[12px] font-code text-r-dim">No spam. Just one email when we're ready.</p>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="border-t border-r-border bg-white">
      <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-r-ink rounded-lg flex items-center justify-center"><Mail size={12} className="text-white" /></div>
          <span className="font-display font-bold text-[16px] text-r-ink">Resolved</span>
        </div>
        <p className="text-[12px] text-r-dim font-code">© 2026 Resolved, Inc.</p>
        <a href="mailto:hello@resolved.email" className="text-[12px] font-code text-r-muted hover:text-r-text transition-colors">hello@resolved.email</a>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────────
export function App() {
  return (
    <div className="bg-white text-r-text">
      <Nav />
      <HeroSection />
      <ProblemSection />
      <HowItWorksSection />
      <FeaturesSection />
      <StoryboardSection />
      <PricingSection />
      <WaitlistSection />
      <Footer />
    </div>
  );
}

export default App;
